import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import { useLocalFilters } from "../../../hooks/useLocalFilters";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useOcelMetadata } from "../../../stores/OcelContext";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import LocalFiltersActiveBadge from "../../../components/common/LocalFiltersActiveBadge";
import { HoverInfoCard, TooltipProvider } from "../../../components/common/tooltip/TooltipSurfaces";
import ObjectAttributeTable, { TOOLTIP_OBJECT_LIMIT } from "../../../components/common/ObjectAttributeTable";
import { createFilteredRequest, makePostRequest } from "../../../services/apiClient";
import AggregationKPISettings from "./AggregationKPISettings";
import { formatAttributeValue } from "../../visualization/GanttChart/ganttTransform";
import { formatBerlinDisplay } from "../../../utils/berlinDate";
import {
  ALL_TIME_PRESET,
  resolveCurrentRange,
  resolvePreviousRange,
  formatRangeLabel,
  formatComparisonCaption,
  detectPeriodPresetFromDateRange,
} from "../_shared/periodPresets";
import {
  KPIValueRow,
  KPIValueColumn,
  KPIValueSlot,
  KPITrendSlot,
  KPIBigValue,
  KPICaption,
  KPITrendBadge,
} from "../_shared/kpiTrendDisplay";

const formatValue = (num) =>
  Number(num).toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatTrendPercent = (trend) => {
  // Vorperiode war 0 — eine prozentuale Änderung ist mathematisch nicht
  // definiert (Division durch 0), also explizit als "kein Wert" kennzeichnen
  // statt eine nackte, einheitenlose Zahl zu zeigen, die leicht mit einem
  // Prozentwert verwechselt wird.
  if (trend.percent === null) return "Kein Wert";
  const sign = trend.percent > 0 ? "+" : trend.percent < 0 ? "−" : "";
  return `${sign}${Math.abs(trend.percent).toFixed(1)} %`;
};

const AggregationKPI = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    currentFilters,
    globalFilterState,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const metadata = useOcelMetadata(activeFile);

  // Component configuration
  const [componentTitle, setComponentTitle] = useState("");
  const [objectType, setObjectType] = useState("");
  const [selectedAttribute, setSelectedAttribute] = useState("");
  const [tooltipAttributes, setTooltipAttributes] = useState([]);

  // Betrachtungszeitraum / Vorperioden-Vergleich configuration
  const [periodPreset, setPeriodPreset] = useState(ALL_TIME_PRESET.key);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showPeriodLabel, setShowPeriodLabel] = useState(true);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [trendDirection, setTrendDirection] = useState("higherIsBetter");

  // Data state
  const [kpiData, setKpiData] = useState(null);
  const [previousKpiData, setPreviousKpiData] = useState(null);
  const [resolvedRange, setResolvedRange] = useState(null);
  const [resolvedPreviousRange, setResolvedPreviousRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);

  // Modal
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Derived metadata — availableObjectTypes filtered by global object-type filter only
  // (local objectTypes filter must not restrict settings dropdown options)
  const [availableObjectTypes, setAvailableObjectTypes] = useState([]);

  useEffect(() => {
    if (!metadata?.object_types) return;
    const allTypes = Object.keys(metadata.object_types).sort();
    const filtered =
      globalFilterState?.objectTypes?.length > 0
        ? allTypes.filter((t) => globalFilterState.objectTypes.includes(t))
        : allTypes;
    setAvailableObjectTypes(filtered);
    // Auto-reset if selected type is no longer available under global filters
    if (objectType && !filtered.includes(objectType)) {
      setObjectType(filtered[0] ?? "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterState?.objectTypes, metadata]);

  const availableActivities = useMemo(
    () => (metadata?.activity_types ? Object.keys(metadata.activity_types).sort() : []),
    [metadata]
  );

  // Unit + type lookup — straight from the OCEL schema already on metadata,
  // scoped to the selected object type when set (mirrors GanttChart.jsx /
  // TracePivotTable.jsx). Type feeds the tooltip object table's per-cell
  // formatting (timestamp vs numeric vs plain string).
  const attributeUnitMap = useMemo(() => {
    const map = {};
    const schemas = objectType
      ? (metadata?.object_types_schema ?? []).filter((t) => t.name === objectType)
      : (metadata?.object_types_schema ?? []);
    for (const t of schemas) {
      for (const attr of (t.attributes ?? [])) {
        if (attr.unit) map[attr.name] = attr.unit;
      }
    }
    return map;
  }, [metadata, objectType]);
  const unit = selectedAttribute ? attributeUnitMap[selectedAttribute] : null;

  const attributeTypeMap = useMemo(() => {
    const map = {};
    const schemas = objectType
      ? (metadata?.object_types_schema ?? []).filter((t) => t.name === objectType)
      : (metadata?.object_types_schema ?? []);
    for (const t of schemas) {
      for (const attr of (t.attributes ?? [])) {
        if (attr.type) map[attr.name] = attr.type;
      }
    }
    return map;
  }, [metadata, objectType]);

  // Tooltip object table cell formatting — missing values render as an empty
  // string (cell still shown, per spec), timestamps use the same Berlin
  // display format used elsewhere in the app, everything else reuses Gantt's
  // shared numeric/unit formatter instead of reinventing it here.
  const formatCellValue = useCallback((attr, value) => {
    if (value == null) return "";
    const type = attributeTypeMap[attr];
    if (type === "timestamp" || type === "date") {
      return formatBerlinDisplay(value) ?? "";
    }
    const isNumericAttr = type === "float" || type === "integer" || type === "double" || type === "number";
    return formatAttributeValue(value, {
      hasDecimals: true,
      unit: attributeUnitMap[attr],
      isNumericAttr,
    });
  }, [attributeTypeMap, attributeUnitMap]);

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.selectedAttribute !== undefined) setSelectedAttribute(initialSettings.selectedAttribute);
    if (initialSettings.tooltipAttributes !== undefined) setTooltipAttributes(initialSettings.tooltipAttributes);
    if (initialSettings.periodPreset !== undefined) {
      setPeriodPreset(initialSettings.periodPreset);
    } else {
      // Configs saved before periodPreset existed — derive a best-guess preset
      // from the legacy resolved dateRange so old tiles keep working unchanged.
      setPeriodPreset(detectPeriodPresetFromDateRange(initialSettings.localFilters?.dateRange));
    }
    if (initialSettings.customStart !== undefined) setCustomStart(initialSettings.customStart);
    if (initialSettings.customEnd !== undefined) setCustomEnd(initialSettings.customEnd);
    if (initialSettings.showPeriodLabel !== undefined) setShowPeriodLabel(initialSettings.showPeriodLabel);
    if (initialSettings.comparisonEnabled !== undefined) setComparisonEnabled(initialSettings.comparisonEnabled);
    if (initialSettings.trendDirection !== undefined) setTrendDirection(initialSettings.trendDirection);
    setPendingApply(true);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      componentTitle, objectType, selectedAttribute, tooltipAttributes, localFilters,
      periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
    }));
  }, [
    componentTitle, objectType, selectedAttribute, tooltipAttributes, localFilters,
    periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Settings modal lifecycle ───────────────────────────────────────────────
  const handleOpenSettings = useCallback(() => {
    startDraftMode();
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback(
    (val) => {
      setIsSettingsModalOpen(val);
      if (!val) cancelDraftFilters();
    },
    [cancelDraftFilters]
  );

  // ── Data fetching ──────────────────────────────────────────────────────────
  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!selectedAttribute || !activeFile) return;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const now = new Date();
      const currentRange = resolveCurrentRange(periodPreset, { customStart, customEnd }, now);
      const previousRange = comparisonEnabled
        ? resolvePreviousRange(periodPreset, currentRange, now)
        : null;

      // A non-"all" period is itself a local override, same as any other
      // local filter — decide local-vs-global the same way useLocalFilters
      // does, but without depending on this render's (possibly stale) value.
      const usesLocalFilters = hasLocalFilters || periodPreset !== ALL_TIME_PRESET.key;
      const filterBase = usesLocalFilters ? localFilters : globalFilterState;

      // Tooltip object table (see Tooltip A below) rides the same request that
      // fetches the sum — only the current-period request needs the extra
      // columns/rows, since the previous-period value never renders a table.
      // wantsObjectRows uses the raw configured list (a user may legitimately
      // configure only the aggregated attribute itself as the sole tooltip
      // column, e.g. "ID + summed value per object") — dedup only applies to
      // the request payload, not to whether rows are fetched at all.
      const wantsObjectRows = tooltipAttributes.length > 0;
      const extraAttrs = tooltipAttributes.filter((a) => a !== selectedAttribute);

      const currentBaseRequest = {
        file_type: activeFile,
        object_type: objectType || undefined,
        selected_attributes: wantsObjectRows ? [selectedAttribute, ...extraAttrs] : [selectedAttribute],
        limit: wantsObjectRows ? TOOLTIP_OBJECT_LIMIT : 1,
        offset: 0,
      };
      const previousBaseRequest = {
        file_type: activeFile,
        object_type: objectType || undefined,
        selected_attributes: [selectedAttribute],
        limit: 1,
        offset: 0,
      };

      const currentRequest = createFilteredRequest(currentBaseRequest, { ...filterBase, dateRange: currentRange });
      const fetches = [makePostRequest("/ocel/object-table", currentRequest, {}, "Aggregation KPI")];

      if (previousRange) {
        const previousRequest = createFilteredRequest(previousBaseRequest, { ...filterBase, dateRange: previousRange });
        fetches.push(makePostRequest("/ocel/object-table", previousRequest, {}, "Aggregation KPI (Vorperiode)"));
      } else {
        fetches.push(Promise.resolve(null));
      }

      const [currentResponse, previousResponse] = await Promise.all(fetches);
      if (seq !== fetchSeqRef.current) return;
      setKpiData(currentResponse);
      setPreviousKpiData(previousResponse);
      setResolvedRange(currentRange);
      setResolvedPreviousRange(previousRange);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err?.message || "Failed to load aggregation");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [
    activeFile, objectType, selectedAttribute, tooltipAttributes, periodPreset, customStart, customEnd,
    comparisonEnabled, hasLocalFilters, localFilters, globalFilterState,
  ]);

  // Fire after Apply commits new config + filters
  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApply]);

  // Auto-reload when filters or active file changes (after first load)
  useEffect(() => {
    if (hasFetched) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, activeFile]);

  useAutoLoad(fetchData, [activeFile, currentFilters], kpiData);

  useEffect(() => {
    if (onRefresh) onRefresh(fetchData);
  }, [onRefresh, fetchData]);

  // A non-"all" period is a local override in its own right, but it no longer
  // lives inside localFilters.dateRange (see fetchData) — reflect it in the
  // badge here instead of writing it back into hook state.
  const periodIsLocalOverride = periodPreset !== ALL_TIME_PRESET.key;
  const displayHasLocalFilters = hasLocalFilters || periodIsLocalOverride;
  const displayLocalFilterCount = localFilterCount + (periodIsLocalOverride ? 1 : 0);

  // ── Header actions ─────────────────────────────────────────────────────────
  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {displayHasLocalFilters && (
          <LocalFiltersActiveBadge
            hasLocalFilters={displayHasLocalFilters}
            localFilterCount={displayLocalFilterCount}
            onClearLocalFilters={clearLocalFilters}
          />
        )}
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={t('common:actions.openSettings')}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings, displayHasLocalFilters, displayLocalFilterCount, clearLocalFilters]
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  useEffect(() => {
    if (onTitleChange) onTitleChange(componentTitle || "Aggregation KPI");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayHasLocalFilters, componentTitle]); // Re-run when badge appears/disappears, or when a restored title is applied

  // ── Derived display values ─────────────────────────────────────────────────
  const sumValue = kpiData?.aggregations?.numeric_sums?.[selectedAttribute];
  const totalObjects = kpiData?.aggregations?.total_objects ?? 0;
  const isNumeric = sumValue !== null && sumValue !== undefined;

  // Tooltip A's object table (see below) — kpiData.rows only carries the full
  // row set when tooltipAttributes is configured (fetchData bumps limit/attrs
  // accordingly); with no configured attributes it stays the pre-existing
  // limit:1 fetch and no table is shown at all.
  const tooltipObjectRows = kpiData?.rows ?? [];
  const showObjectTooltipTable = tooltipAttributes.length > 0;

  // "Gesamter Zeitraum" has no meaningful Vorperiode at all — treat comparison
  // as off regardless of the raw stored flag, so a tile switched to this
  // preset (without the user separately unchecking the box, which the
  // Settings UI already disables/greys out) doesn't show a dangling "Kein
  // Vergleichswert verfügbar" placeholder for a comparison that was never
  // possible in the first place.
  const effectiveComparisonEnabled = comparisonEnabled && periodPreset !== ALL_TIME_PRESET.key;

  const previousSumValue = previousKpiData?.aggregations?.numeric_sums?.[selectedAttribute];
  const hasPreviousValue = effectiveComparisonEnabled && previousSumValue !== null && previousSumValue !== undefined;

  const periodLabel = showPeriodLabel && resolvedRange
    ? formatRangeLabel(resolvedRange)
    : null;

  const previousPeriodLabel = resolvedPreviousRange
    ? formatRangeLabel(resolvedPreviousRange)
    : null;

  const comparisonCaption = previousPeriodLabel
    ? formatComparisonCaption(periodPreset, previousPeriodLabel)
    : null;

  const trend = useMemo(() => {
    if (!effectiveComparisonEnabled || !isNumeric || !hasPreviousValue) return null;
    const diff = sumValue - previousSumValue;
    // Vorperiode war 0 — keine sinnvolle prozentuale Richtung berechenbar,
    // also neutral darstellen statt eine (nicht belastbare) Verbesserung/
    // Verschlechterung zu suggerieren. formatTrendPercent zeigt dafür
    // "Kein Wert" statt einer Prozentzahl.
    if (previousSumValue === 0) {
      return { diff, direction: "flat", color: "neutral", percent: null };
    }
    const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
    const improved = trendDirection === "higherIsBetter" ? diff > 0 : diff < 0;
    const worsened = trendDirection === "higherIsBetter" ? diff < 0 : diff > 0;
    const color = diff === 0 ? "neutral" : improved ? "positive" : worsened ? "negative" : "neutral";
    const percent = (diff / Math.abs(previousSumValue)) * 100;
    return { diff, direction, color, percent };
  }, [effectiveComparisonEnabled, isNumeric, hasPreviousValue, sumValue, previousSumValue, trendDirection]);

  return (
    <BaseTile loading={loading} error={error} minHeight="sm" padding="sm">
      {/* Placeholder when not yet configured */}
      {!hasFetched && !loading && !error && (
        <TileStatusContent
          status="info"
          title="Configure Aggregation KPI"
          message="Click the gear icon above to select an attribute and configure this tile."
          icon={
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      )}

      {/* KPI display */}
      {hasFetched && !loading && !error && kpiData && (
        <TileContent align="stretch" justify="start">
          <div className="w-full flex flex-col items-center gap-1 py-1">
            {isNumeric ? (
              <TooltipProvider>
                <div className="text-sm font-medium text-gray-700">{selectedAttribute}</div>

                <KPIValueRow>
                  <KPIValueColumn>
                    <HoverInfoCard
                      variant={showObjectTooltipTable ? "card" : "plain"}
                      trigger={
                        <KPIValueSlot className="gap-1.5 cursor-default">
                          <KPIBigValue rawValue={sumValue}>{formatValue(sumValue)}</KPIBigValue>
                          {unit && <span className="text-lg font-medium text-gray-500">{unit}</span>}
                        </KPIValueSlot>
                      }
                    >
                      <div className={showObjectTooltipTable ? "px-3.5 py-2 text-[0.72rem] font-medium text-[#0f172a] whitespace-nowrap" : undefined}>
                        SUM({selectedAttribute}{unit ? ` (${unit})` : ""}) · {totalObjects.toLocaleString()} Objekte
                      </div>
                      {showObjectTooltipTable && (
                        <ObjectAttributeTable
                          attributes={tooltipAttributes}
                          rows={tooltipObjectRows}
                          totalCount={totalObjects}
                          formatCellValue={formatCellValue}
                        />
                      )}
                    </HoverInfoCard>
                    {periodLabel && <KPICaption>{periodLabel}</KPICaption>}
                  </KPIValueColumn>

                  {effectiveComparisonEnabled && (
                    <KPIValueColumn>
                      {trend ? (
                        <HoverInfoCard
                          variant="plain"
                          trigger={
                            <KPITrendSlot className="cursor-default">
                              <KPITrendBadge trend={trend}>{formatTrendPercent(trend)}</KPITrendBadge>
                            </KPITrendSlot>
                          }
                        >
                          <div>Aktuell: {formatValue(sumValue)}{unit ? ` ${unit}` : ""}</div>
                          <div>Vorperiode: {formatValue(previousSumValue)}{unit ? ` ${unit}` : ""}</div>
                          {previousPeriodLabel && <div>{previousPeriodLabel}</div>}
                        </HoverInfoCard>
                      ) : (
                        <KPITrendSlot>
                          <KPICaption>Kein Vergleichswert verfügbar</KPICaption>
                        </KPITrendSlot>
                      )}

                      {trend && comparisonCaption && <KPICaption>{comparisonCaption}</KPICaption>}
                    </KPIValueColumn>
                  )}
                </KPIValueRow>
              </TooltipProvider>
            ) : (
              <>
                <div className="text-sm font-medium text-gray-700">{selectedAttribute}</div>
                <div className="text-5xl font-semibold text-gray-400">—</div>
                <div className="text-sm text-gray-400">No numeric data for this attribute</div>
              </>
            )}
          </div>
        </TileContent>
      )}

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        title="Aggregation KPI Settings"
        getInitialValues={() => ({
          componentTitle, objectType, selectedAttribute, tooltipAttributes,
          periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
        })}
        onApply={(values) => {
          setComponentTitle(values.componentTitle);
          setObjectType(values.objectType);
          setSelectedAttribute(values.selectedAttribute);
          setTooltipAttributes(values.tooltipAttributes ?? []);
          setPeriodPreset(values.periodPreset);
          setCustomStart(values.customStart);
          setCustomEnd(values.customEnd);
          setShowPeriodLabel(values.showPeriodLabel);
          setComparisonEnabled(values.comparisonEnabled);
          setTrendDirection(values.trendDirection);
          if (onTitleChange && values.componentTitle) {
            onTitleChange(values.componentTitle);
          }
          applyDraftFilters();
          setPendingApply(true);
        }}
        applyButtonText="Apply & Load"
        isApplyDisabled={false}
      >
        {({ values, setField }) => (
          <AggregationKPISettings
            componentTitle={values.componentTitle}
            onTitleChange={(v) => setField("componentTitle", v)}
            objectType={values.objectType}
            onObjectTypeChange={(v) => setField("objectType", v)}
            selectedAttribute={values.selectedAttribute}
            onSelectedAttributeChange={(v) => setField("selectedAttribute", v)}
            tooltipAttributes={values.tooltipAttributes ?? []}
            onTooltipAttributesChange={(v) => setField("tooltipAttributes", v)}
            availableObjectTypes={availableObjectTypes}
            availableActivities={availableActivities}
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            activeFile={activeFile}
            loading={loading}
            periodPreset={values.periodPreset}
            onPeriodPresetChange={(v) => setField("periodPreset", v)}
            customStart={values.customStart}
            onCustomStartChange={(v) => setField("customStart", v)}
            customEnd={values.customEnd}
            onCustomEndChange={(v) => setField("customEnd", v)}
            showPeriodLabel={values.showPeriodLabel}
            onShowPeriodLabelChange={(v) => setField("showPeriodLabel", v)}
            comparisonEnabled={values.comparisonEnabled}
            onComparisonEnabledChange={(v) => setField("comparisonEnabled", v)}
            trendDirection={values.trendDirection}
            onTrendDirectionChange={(v) => setField("trendDirection", v)}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
};

export default AggregationKPI;
